import { useCallback, useState } from 'react';
import type { JSX } from 'react';

import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import CardActions from '@mui/material/CardActions';
import Tooltip from '@mui/material/Tooltip';
import Menu from '@mui/material/Menu';
import IconButton from '@mui/material/IconButton';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import RoomServiceIcon from '@mui/icons-material/RoomService';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { css } from '@emotion/react';

import { ContentOriginEnum } from '@e-socna/data-layer';
import { buildPath, ensureDefined, invariant } from '@e-socna/utils';
import { EMPTY_ID } from '@e-socna/empty-client';
import { EMPTY_ARRAY, EMPTY_OBJECT } from '@e-socna/empty';
import { colorGenerator } from '@e-socna/colors';
import type { ContentOriginId } from '@e-socna/data-layer';

import { useIsPricingEditable } from '../../../contexts/PricingEditionContext/hooks/useIsPricingEditable.hook';
import { useReferencesContext } from '../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { useFindOneResolvableServiceQuery } from '../../../pages/ResolvableService/hooks/useFindOneResolvableServiceQuery.hook';
import { PATH_RESOLVABLE_SERVICE_DEFAULT } from '../../../app/constants/paths.constant';

import { LoadingCard } from '../../LoadingCard';
import { ConsultButton } from '../../ConsultButton';
import { MenuItem } from '../../Menu/MenuItem';
import { useActionMenu } from '../../Menu/hooks/useActionMenu.hook';
import { colorToPaletteColor } from '../../../utils/color-to-palette-color.util';
import { iconToMuiIcon } from '../../../utils/icon-to-mui-icon.util';
import { Avatar } from '../../Avatar';
import { ExpandMoreCard } from '../../ExpandMoreCard';
import type { Option } from '../../Menu/MenuItem/types/option.type';

import { useGetServiceUsageData } from '../hooks/useGetServiceUsageData.hook';
import {
  SERVICE_USAGE_CARD_SIZE,
  SERVICE_USAGE_CARD_SX,
} from '../constants/card.constant';
import { useGetServiceData } from '../hooks/useGetServiceData.hook';
import type { CardProps, ServiceType } from '../types/props.type';

import { MoreDetails } from './MoreDetails';
import { ResolvableServiceInfos } from './ResolvableServiceInfos';
import { ServiceInfos } from './ServiceInfos';
import { ResolvableServiceUsageInfos } from './ResolvableServiceUsageInfos';
import { ResolvableServiceUsageQuotationSettings } from './ResolvableServiceUsageQuotationSettings';
import { ServiceUsageInfos } from './ServiceUsageInfos';
import { ServiceUsageQuotationSettings } from './ServiceUsageQuotationSettings';
import { getServiceCode } from './utils/get-service-code.util';
import type { ServiceUsageCardCmp_ResolvableServiceBaseInterface } from './fragments/resolvable-service.fragment';
import type { ServiceUsageCardCmp_Service } from './fragments/service.fragment';
import type { ServiceUsageCardCmp_ServiceUsage } from './fragments/service-usage.fragment';
import type { ServiceUsageCardCmp_ResolvableServiceUsage } from './fragments/resolvable-service-usage.fragment';

export const ServiceUsageCard = <R extends boolean>({
  consultResolvableServiceInNewTab,
  editionMode,
  fallbackServiceUsage,
  isInResolvableMode,
  maskAmounts = false,
  onStartCustomizeOne: handleStartCustomize,
  onStartFlushOne: handleStartFlush,
  onStartMove: handleStartMove,
  onStartRemove: handleStartRemove,
  onStartUpdate: handleStartUpdate,
  resolvableFieldSettings = EMPTY_OBJECT,
  serviceUsage: pServiceUsage,
}: Readonly<CardProps<R>>) => {
  const { contentOriginsCodeMap, contentOriginsIdMap } = useReferencesContext();
  const isPricingEditable = useIsPricingEditable();

  const getServiceUsageData = useGetServiceUsageData();
  const getServiceData = useGetServiceData();
  const serviceUsageData = getServiceUsageData(
    isInResolvableMode,
    pServiceUsage,
  );

  const { serviceUsageOrFallback, contentOrigin } = ensureDefined({
    // A compiled usage (quotation snapshot) carries its frozen service even
    // when it comes from the catalog: only a resolvable one inherits it.
    serviceUsageOrFallback: getServiceUsageData(
      isInResolvableMode,
      isInResolvableMode &&
        serviceUsageData?.contentOriginId === contentOriginsCodeMap.Catalog._id
        ? fallbackServiceUsage
        : (pServiceUsage ?? fallbackServiceUsage),
    ),
    contentOrigin: serviceUsageData
      ? contentOriginsIdMap[serviceUsageData.contentOriginId as ContentOriginId]
      : contentOriginsCodeMap.Catalog,
  });

  invariant(!!serviceUsageOrFallback, 'Service usage is not defined');

  const serviceOrigin = serviceUsageOrFallback.serviceOriginId
    ? contentOriginsIdMap[
        serviceUsageOrFallback.serviceOriginId as ContentOriginId
      ]
    : undefined;

  const {
    anchorEl,
    handleCloseActionMenu,
    handleOpenActionMenu,
    isActionMenuOpen,
    options,
  } = useActionMenu({
    id: serviceUsageData?._id ?? EMPTY_ID,
    ...(editionMode && serviceUsageData?._id
      ? {
          onStartCustomize:
            contentOrigin.code === ContentOriginEnum.Catalog
              ? handleStartCustomize
              : undefined,
          onStartFlush:
            contentOrigin.code === ContentOriginEnum.Modified
              ? handleStartFlush
              : undefined,
          onStartRemove: handleStartRemove,
          onStartUpdate:
            contentOrigin.code !== ContentOriginEnum.Catalog
              ? handleStartUpdate
              : undefined,
        }
      : EMPTY_OBJECT),
  });

  const moveOptions: readonly Option[] =
    editionMode && handleStartMove && serviceUsageData?._id
      ? [
          {
            action: () => {
              handleStartMove(serviceUsageData._id);
            },
            children: (
              <>
                <ListItemIcon>
                  <DriveFileMoveIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Déplacer vers un autre groupe</ListItemText>
              </>
            ),
            key: 'move',
            title: 'Déplacer vers un autre groupe',
          },
        ]
      : EMPTY_ARRAY;

  const [expanded, setExpanded] = useState(false);
  const handleExpandClick = useCallback(() => {
    setExpanded((prevExpanded) => !prevExpanded);
  }, []);

  const {
    data: rowResolvableServiceData,
    isSuccess: resolvableServiceIsSuccess,
  } = useFindOneResolvableServiceQuery(
    serviceUsageOrFallback.serviceId ?? EMPTY_ID,
  );

  if (!serviceUsageOrFallback.specialService && !resolvableServiceIsSuccess) {
    return (
      <Grid size={SERVICE_USAGE_CARD_SIZE} sx={SERVICE_USAGE_CARD_SX}>
        <LoadingCard />
      </Grid>
    );
  }

  const resolvableAndSpecialCase = () => {
    const service = getServiceData(
      true,
      serviceUsageOrFallback.specialService as ServiceType<true>,
    );

    return {
      isACatalogService: false,
      service,
      serviceInfosCmp: (
        <ResolvableServiceInfos
          maskAmounts={maskAmounts}
          resolvableFieldSettings={resolvableFieldSettings}
          service={service}
        />
      ),
      serviceUsageInfosCmp: (
        <ResolvableServiceUsageInfos
          resolvableFieldSettings={resolvableFieldSettings}
          serviceUsage={
            serviceUsageOrFallback as ServiceUsageCardCmp_ResolvableServiceUsage
          }
        />
      ),
      quotationSettingsCmp: (
        <ResolvableServiceUsageQuotationSettings
          resolvableFieldSettings={resolvableFieldSettings}
          serviceUsage={
            serviceUsageOrFallback as ServiceUsageCardCmp_ResolvableServiceUsage
          }
        />
      ),
    };
  };

  const normalAndSpecialCase = () => {
    const service = getServiceData(
      false,
      serviceUsageOrFallback.specialService as ServiceType<false>,
    );

    return {
      isACatalogService: false,
      service,
      serviceInfosCmp: (
        <ServiceInfos maskAmounts={maskAmounts} service={service} />
      ),
      serviceUsageInfosCmp: (
        <ServiceUsageInfos
          serviceUsage={
            serviceUsageOrFallback as ServiceUsageCardCmp_ServiceUsage
          }
        />
      ),
      quotationSettingsCmp: (
        <ServiceUsageQuotationSettings
          serviceUsage={
            serviceUsageOrFallback as ServiceUsageCardCmp_ServiceUsage
          }
        />
      ),
    };
  };

  const resolvableAndCatalogCase = () => {
    invariant(
      resolvableServiceIsSuccess,
      'Resolvable service data is not available',
    );

    const service = getServiceData(
      true,
      rowResolvableServiceData.findOneResolvableService as ServiceType<true>,
    );

    return {
      isACatalogService: true,
      service,
      serviceInfosCmp: (
        <ResolvableServiceInfos
          maskAmounts={maskAmounts}
          resolvableFieldSettings={resolvableFieldSettings}
          service={service}
        />
      ),
      serviceUsageInfosCmp: (
        <ResolvableServiceUsageInfos
          resolvableFieldSettings={resolvableFieldSettings}
          serviceUsage={
            serviceUsageOrFallback as ServiceUsageCardCmp_ResolvableServiceUsage
          }
        />
      ),
      quotationSettingsCmp: (
        <ResolvableServiceUsageQuotationSettings
          resolvableFieldSettings={resolvableFieldSettings}
          serviceUsage={
            serviceUsageOrFallback as ServiceUsageCardCmp_ResolvableServiceUsage
          }
        />
      ),
    };
  };

  const normalAndCatalogCase = () => {
    invariant(
      resolvableServiceIsSuccess,
      'Resolvable service data is not available',
    );

    const service = getServiceData(
      false,
      rowResolvableServiceData.findOneResolvableService as ServiceType<false>,
    );

    return {
      isACatalogService: true,
      service,
      serviceInfosCmp: (
        <ServiceInfos maskAmounts={maskAmounts} service={service} />
      ),
      serviceUsageInfosCmp: (
        <ServiceUsageInfos
          serviceUsage={
            serviceUsageOrFallback as ServiceUsageCardCmp_ServiceUsage
          }
        />
      ),
      quotationSettingsCmp: (
        <ServiceUsageQuotationSettings
          serviceUsage={
            serviceUsageOrFallback as ServiceUsageCardCmp_ServiceUsage
          }
        />
      ),
    };
  };

  const {
    isACatalogService,
    quotationSettingsCmp,
    service,
    serviceInfosCmp,
    serviceUsageInfosCmp,
  } = ensureDefined<{
    isACatalogService: boolean;
    service:
      | ServiceUsageCardCmp_ResolvableServiceBaseInterface
      | ServiceUsageCardCmp_Service;
    serviceInfosCmp: JSX.Element;
    serviceUsageInfosCmp: JSX.Element;
    quotationSettingsCmp: JSX.Element;
  }>(
    isInResolvableMode && serviceUsageOrFallback.specialService
      ? resolvableAndSpecialCase()
      : !isInResolvableMode && serviceUsageOrFallback.specialService
        ? normalAndSpecialCase()
        : isInResolvableMode && resolvableServiceIsSuccess
          ? resolvableAndCatalogCase()
          : !isInResolvableMode && resolvableServiceIsSuccess
            ? normalAndCatalogCase()
            : {
                isACatalogService: undefined,
                service: undefined,
                serviceInfosCmp: undefined,
                serviceUsageInfosCmp: undefined,
                quotationSettingsCmp: undefined,
              },
  );

  const path = buildPath(PATH_RESOLVABLE_SERVICE_DEFAULT, { id: service._id });

  return (
    <Grid size={SERVICE_USAGE_CARD_SIZE} sx={SERVICE_USAGE_CARD_SX}>
      <Card
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
        }}
      >
        <CardHeader
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...(editionMode
            ? {
                action: (
                  <>
                    <Tooltip title="Actions sur la prestation">
                      <IconButton
                        aria-controls={
                          isActionMenuOpen
                            ? `${serviceUsageOrFallback._id}-actions-menu`
                            : undefined
                        }
                        aria-expanded={isActionMenuOpen ? 'true' : undefined}
                        aria-haspopup="true"
                        aria-label="Actions sur la prestation"
                        disabled={!isPricingEditable}
                        id={`${serviceUsageOrFallback._id}-actions-menu-button`}
                        onClick={handleOpenActionMenu}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </Tooltip>
                    <Menu
                      anchorEl={anchorEl}
                      id={`${serviceUsageOrFallback._id}-actions-menu`}
                      onClose={handleCloseActionMenu}
                      open={isActionMenuOpen}
                      slotProps={{
                        list: {
                          'aria-labelledby': `${serviceUsageOrFallback._id}-actions-menu-button`,
                        },
                      }}
                    >
                      {[...options, ...moveOptions].map((option: Option) => (
                        <MenuItem
                          key={option.key}
                          onCloseMenu={handleCloseActionMenu}
                          option={option}
                        />
                      ))}
                    </Menu>
                  </>
                ),
              }
            : EMPTY_OBJECT)}
          avatar={
            <Stack direction="row" spacing={1}>
              <Avatar
                bgColor={colorGenerator(getServiceCode(service))}
                fullName={getServiceCode(service)}
                icon={<RoomServiceIcon />}
                label="Avatar de la Prestation"
              />
              <Avatar
                bgColor={colorToPaletteColor(contentOrigin.chip.color)}
                icon={iconToMuiIcon(contentOrigin.chip.icon)}
                label={`Origine de la prestation : ${contentOrigin.label}`}
              />
              {!!serviceOrigin && (
                <Avatar
                  bgColor={colorToPaletteColor(serviceOrigin.chip.color)}
                  icon={iconToMuiIcon(serviceOrigin.chip.icon)}
                  label={`Origine du service : ${serviceOrigin.label}`}
                />
              )}
            </Stack>
          }
          css={css`
            .MuiCardHeader-action {
              display: contents;
            }
          `}
        />
        <CardContent
          sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}
        >
          {serviceInfosCmp}
          {serviceUsageInfosCmp}
        </CardContent>
        <CardActions disableSpacing>
          {!isACatalogService ? (
            <></>
          ) : (
            <ConsultButton
              inNewTab={consultResolvableServiceInNewTab}
              to={path}
            />
          )}
          <ExpandMoreCard
            expanded={expanded}
            onExpandClick={handleExpandClick}
          />
        </CardActions>
        <MoreDetails
          expanded={expanded}
          quotationSettings={quotationSettingsCmp}
          service={service}
        />
      </Card>
    </Grid>
  );
};
